<template>
  <section class="relative">
    <div :id="galleryId" qaid="product-images-component" />
    <Transition name="fade">
      <p v-if="isAccessorized" class="absolute top-1 right-1">
        <pdl-label kind="prominent" qaid="product-images-accessorized-callout">
          {{ accessoryTranslations }}
        </pdl-label>
      </p>
    </Transition>
  </section>
</template>

<script>
import headTag from '@/utils/head-tag';
import {Cloudinary} from '@/constants/cloudinary';
import {PdlLabel} from '@pedal/pdl-label';
import {mapState} from 'vuex';

const CLOUDINARY_SCRIPT_ID = 'cloudinary-gallery-script';

export default {
  components: {
    PdlLabel,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeIndex: 0,
      cloudGallery: null,
      isScriptLoaded: false,
      galleryObserver: null,
      galleryId: `product-images-gallery-${Math.random().toString(20).substring(2, 6)}`,
    };
  },
  computed: {
    ...mapState('global', ['isCompactView']),
    thumbnailGrid() {
      if (this.images.length <= 4) {
        return 'grid-cols-4';
      } else if (this.images.length === 5) {
        return 'grid-cols-5';
      }
      return '';
    },
    accessoryTranslations() {
      return this.images?.[0]?.productType === 'bike'
        ? this.$t('product.detail.image.accessorized.caption')
        : this.$t('product.detail.image.accessorizedAftermarket.caption');
    },
    isAccessorized() {
      return this.images.find((image, i) => {
        return i === this.activeIndex && image?.accessorized ? true : false;
      });
    },
  },
  created() {
    if (!document.getElementById(CLOUDINARY_SCRIPT_ID)) {
      headTag.appendAsyncScript(CLOUDINARY_SCRIPT_ID, 'https://product-gallery.cloudinary.com/latest/all.js');
    } else {
      this.isScriptLoaded = true;
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (!this.isScriptLoaded) {
        const script = document.getElementById(CLOUDINARY_SCRIPT_ID);
        script.addEventListener('load', () => {
          this.initCloudGallery();
        });
        return;
      }
      this.initCloudGallery();
    });
  },
  unmounted() {
    if (this.cloudGallery) {
      this.cloudGallery.destroy();
    }
    if (this.galleryObserver) {
      this.galleryObserver.disconnect();
    }

    const script = document.getElementById(CLOUDINARY_SCRIPT_ID);
    if (script) {
      script.removeEventListener('load', () => {});
    }
  },
  methods: {
    imageMap(images) {
      return images.map((image) => ({
        publicId: image?.assetId,
        mediaType: image?.type ?? 'image',
        altText: '', // Update when we get alt text with translations.
        accessorized: image?.accessorized ?? false,
      }));
    },
    initCloudGallery() {
      //eslint-disable-next-line
      this.cloudGallery = cloudinary.galleryWidget({
        container: `#${this.galleryId}`,
        cloudName: Cloudinary.CLOUD_NAME,
        mediaAssets: this.imageMap(this.images),
        themeProps: {
          active: '#808080',
        },
        viewportBreakpoints: [
          {breakpoint: 1024, carouselStyle: 'indicators', carouselLocation: 'bottom', navigation: 'none'},
        ],
        accessibilityProps: {
          image: {
            alt: (image) => image.altText ?? '',
          },
        },
        navigation: 'mouseover',
        carouselLocation: 'bottom',
        aspectRatio: this.isCompactView ? '16:9' : '4:3',
        indicatorShape: 'radius',
        imageBreakpoint: 200,
        thumbnailProps: {
          selectedBorderPosition: 'bottom',
          selectedBorderColor: 'transparent',
          borderColor: 'transparent',
          width: 148.5,
          height: 148.5,
          spacing: 8,
          navigationFloat: true,
          navigationShape: 'rectangle',
          navigationSize: 48,
        },
        navigationButtonProps: {
          shape: 'round',
          iconColor: '#000',
          color: '#fff',
          size: 42,
        },
        navigationPosition: 'offset',
        navigationOffset: 4,
        loaderProps: {
          style: 'circle',
        },
      });
      this.cloudGallery.render();
      this.observeGalleryChanges();
    },
    observeGalleryChanges() {
      this.galleryObserver = new MutationObserver((mutations) => {
        if (!mutations?.length) return;
        this.$nextTick(() => {
          const resetStyle = (element) => {
            if (!element) return;
            element.style.width = '100%';
            element.style.height = 'auto';
            element.style.marginRight = '0';
            element.style.padding = '0';
            element.removeAttribute('width');
            element.removeAttribute('height');
          };

          const carouselViewer = document.querySelector(
            "[data-test='carousel-viewer-wrap'] [data-test='carousel-inner-wrapper']"
          );
          const carouselThumbnailWrap = document.querySelector(
            "[data-test='gallery-thumbnail-wrap'] [data-test='carousel']"
          );
          const carouselThumbnailInnerWrapper = document.querySelector(
            "[data-test='gallery-thumbnail-wrap'] [data-test='carousel-inner-wrapper']"
          );

          const zoomFlyout = document.querySelector('[data-test="zoom-flyout-wrap"]');

          // Main Carousel Image overrides
          if (carouselViewer) {
            for (const slideItemWrap of carouselViewer.children) {
              slideItemWrap.classList.add('relative');

              slideItemWrap.firstElementChild.firstElementChild.style.width = '100%';
              slideItemWrap.firstElementChild.firstElementChild.style.height = 'auto';
            }
          }

          if (zoomFlyout) {
            zoomFlyout.style.top = 'unset';
            const zoomImg = zoomFlyout.querySelector('img');

            if (zoomImg && !zoomImg.hasAttribute('alt')) {
              const activeImage = this.images[this.activeIndex];
              zoomImg.setAttribute('alt', activeImage?.alt || '');
            }
          }

          // Get active index for accessory callout
          this.activeIndex = this.cloudGallery?.getActiveIndex();

          // Don't grid if more than 5 images (default behavior)
          if (this.images.length > 5) {
            if (carouselThumbnailInnerWrapper) {
              carouselThumbnailInnerWrapper.style.display = 'flex';
              carouselThumbnailInnerWrapper.classList.remove('gap-1', 'xl:gap-2');
            }
            return;
          }

          // Thumbnail overrides
          if (carouselThumbnailWrap) {
            carouselThumbnailWrap.style.height = 'auto';
          }

          if (carouselThumbnailInnerWrapper) {
            carouselThumbnailInnerWrapper.style.height = 'auto';
            carouselThumbnailInnerWrapper.style.width = '100%';
            if (this.images.length <= 1) {
              carouselThumbnailInnerWrapper.style.display = 'none';
            } else {
              carouselThumbnailInnerWrapper.style.display = 'grid';
            }
            carouselThumbnailInnerWrapper.classList.add(this.thumbnailGrid, 'gap-1', 'xl:gap-2');

            for (const slideItemWrap of carouselThumbnailInnerWrapper.children) {
              slideItemWrap.style.display = 'flex';
              slideItemWrap.style.alignItems = 'center';

              resetStyle(slideItemWrap);
              resetStyle(slideItemWrap.firstElementChild);
              resetStyle(slideItemWrap.firstElementChild.firstElementChild);
              resetStyle(slideItemWrap.firstElementChild.firstElementChild.firstElementChild);
            }
          }
        });
      });

      this.galleryObserver.observe(document.getElementById(this.galleryId), {
        childList: true,
        subtree: true,
      });
    },
  },
};
</script>
